/**
 * Users Page - 사용자 관리
 * 
 * 관리자가 사용자 목록을 조회하고 역할을 변경할 수 있는 페이지
 */

import { useState, useEffect } from 'react';
import { Topbar } from '../components/layout';
import { Button, Badge, Modal, Select, SearchInput, SortableTh, Tabs, useTableSort, useToast } from '../components/ui';
import type { TabItem } from '../components/ui';
import { authApi, User, tenantApi, customerApi, teamApi, Tenant, Customer, Team } from '../api';
import { UserRole, normalizeRole, roleLabel, isProductAdminRole } from '../utils/role';
import EditIcon from '../assets/icons/common/edit.svg?react';
import TrashIcon from '../assets/icons/common/trash.svg?react';
import '../styles/adminPage.css';

// 선택 가능한 역할 목록 (등록/수정 드롭다운 · 필터)
const ROLE_OPTIONS: UserRole[] = ['productAdmin', 'tenantAdmin', 'customerAdmin', 'teamAdmin', 'user'];

// 역할별 필요한 소속 범위
const needsTenant = (_role: UserRole) => true;
const needsCustomer = (role: UserRole) => role === 'customerAdmin' || role === 'teamAdmin' || role === 'user';
const needsTeam = (role: UserRole) => role === 'teamAdmin' || role === 'user';

export function UsersPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [needsAdmin, setNeedsAdmin] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ name: '', role: 'user' as UserRole, tenantId: '', customerId: '', teamId: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');
  const [tenantFilter, setTenantFilter] = useState('');
  const sort = useTableSort<'name' | 'email' | 'role' | 'tenant' | 'customer' | 'team' | 'createdAt'>();

  // 테넌트/고객사 목록 (소속 선택용)
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [teams, setTeams] = useState<Team[]>([]); // 팀 선택용 (팀 관리자 역할)

  // 사용자 등록 모달
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', passwordConfirm: '', role: 'user' as UserRole, tenantId: '', customerId: '', teamId: '', note: '' });
  const [adding, setAdding] = useState(false);
  const [addErrors, setAddErrors] = useState<Set<string>>(new Set());

  // 사용자 초대 모달
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmails, setInviteEmails] = useState<string[]>(['']);
  const [inviteRole, setInviteRole] = useState<UserRole>('user');
  const [inviteTenantId, setInviteTenantId] = useState('');
  const [inviteTeamId, setInviteTeamId] = useState('');
  const [inviteCustomerId, setInviteCustomerId] = useState('');
  const [inviteTeams, setInviteTeams] = useState<Team[]>([]);
  const [inviting, setInviting] = useState(false);
  const [inviteErrors, setInviteErrors] = useState<Set<string>>(new Set());
  
  const currentUser = authApi.getUser();
  const isProductAdmin = isProductAdminRole(currentUser?.role);
  const isTenantAdmin = !isProductAdmin && (currentUser?.role === 'tenantAdmin');
  const isCustomerAdmin = currentUser?.role === 'customerAdmin';

  // 초대/등록 시 현재 관리자 소속 기본값
  const myTenantId = currentUser?.tenantId || '';
  const myCustomerId = currentUser?.customerId || '';

  // 검색 + 역할 필터링
  // id → 이름 매핑 (리스트에 소속 이름 표시용)
  const tenantNameMap = new Map(tenants.map((t) => [t.id, t.name]));
  const customerNameMap = new Map(customers.map((c) => [c.id, c.name]));
  const teamNameMap = new Map(teams.map((t) => [t.id, t.name]));

  const filteredUsers = users.filter((user) => {
    if (roleFilter !== 'all' && normalizeRole(user.role) !== roleFilter) return false;
    if (tenantFilter && user.tenantId !== tenantFilter) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const name = (user.name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const role = (user.role || '').toLowerCase();
    return (
      name.includes(query) ||
      email.includes(query) ||
      role.includes(query)
    );
  });

  useEffect(() => {
    loadUsers();
    loadOrgs();
  }, []);

  const loadOrgs = async () => {
    try {
      const [ts, cs, tms] = await Promise.all([
        tenantApi.list().catch(() => []),
        customerApi.list().catch(() => []),
        teamApi.list().catch(() => []),
      ]);
      setTenants(ts);
      setCustomers(cs);
      setTeams(tms);
    } catch { /* 소속 목록 로드 실패 무시 */ }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      setNeedsAdmin(false);
      const data = await authApi.listUsers();
      setUsers(data);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 403) {
          setNeedsAdmin(true);
          return;
        }
      }
      showToast('error', '사용자 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleMakeAdmin = async () => {
    try {
      setPromoting(true);
      await authApi.makeAdmin();
      showToast('success', '관리자로 승격되었습니다. 다시 로그인해주세요.');
      setTimeout(() => {
        authApi.logout();
        window.location.href = '/login';
      }, 1500);
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: { message?: string } } } };
        showToast('error', axiosError.response?.data?.error?.message || '관리자 승격에 실패했습니다.');
      } else {
        showToast('error', '관리자 승격에 실패했습니다.');
      }
    } finally {
      setPromoting(false);
    }
  };

  const handleOpenDetail = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name,
      role: normalizeRole(user.role),
      tenantId: user.tenantId || '',
      customerId: user.customerId || '',
      teamId: user.teamId || '',
      note: user.note || '',
    });
    // 팀 관리자인 경우 팀 목록 로드
    if (user.customerId) {
      teamApi.listByCustomer(user.customerId).then(setTeams).catch(() => setTeams([]));
    }
  };

  const handleCloseDetail = () => {
    setSelectedUser(null);
    setEditForm({ name: '', role: 'user', tenantId: '', customerId: '', teamId: '', note: '' });
  };

  const handleSave = async () => {
    if (!selectedUser) return;

    // 역할별 소속 필수 검증
    if (needsTenant(editForm.role) && !editForm.tenantId) {
      showToast('error', '소속 테넌트를 선택해주세요.');
      return;
    }
    if (needsCustomer(editForm.role) && !editForm.customerId) {
      showToast('error', '소속 고객사를 선택해주세요.');
      return;
    }
    if (needsTeam(editForm.role) && !editForm.teamId) {
      showToast('error', '소속 팀을 선택해주세요.');
      return;
    }

    try {
      setSaving(true);
      // 역할에 맞게 소속 스코핑 (불필요한 값은 비움)
      const payload = {
        name: editForm.name,
        role: editForm.role,
        tenantId: needsTenant(editForm.role) ? editForm.tenantId : '',
        customerId: needsCustomer(editForm.role) ? editForm.customerId : '',
        teamId: needsTeam(editForm.role) ? editForm.teamId : '',
        note: editForm.note,
      };
      const updated = await authApi.updateUser(selectedUser.id, payload);
      setUsers((prev) =>
        prev.map((u) => (u.id === selectedUser.id ? { ...u, ...updated } : u))
      );
      showToast('success', '사용자 정보가 수정되었습니다.');
      handleCloseDetail();
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: { message?: string } } } };
        showToast('error', axiosError.response?.data?.error?.message || '수정에 실패했습니다.');
      } else {
        showToast('error', '수정에 실패했습니다.');
      }
    } finally {
      setSaving(false);
    }
  };

  // 등록 모달 닫기 — 입력값 + 에러 모두 초기화
  const closeAddModal = () => {
    setShowAddModal(false);
    setAddForm({ name: '', email: '', password: '', passwordConfirm: '', role: 'user', tenantId: '', customerId: '', teamId: '', note: '' });
    setAddErrors(new Set());
  };

  const openAddModal = () => {
    // 테넌트/고객사 관리자는 본인 소속 기본값 설정
    setAddForm({ name: '', email: '', password: '', passwordConfirm: '', role: 'user', tenantId: myTenantId, customerId: myCustomerId, teamId: '', note: '' });
    setAddErrors(new Set());
    setShowAddModal(true);
  };

  const closeInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmails(['']);
    setInviteRole('user');
    setInviteTenantId('');
    setInviteCustomerId('');
    setInviteTeamId('');
    setInviteTeams([]);
    setInviteErrors(new Set());
  };

  const openInviteModal = () => {
    // 테넌트/고객사 관리자는 소속 기본값 설정
    setInviteTenantId(myTenantId);
    setInviteCustomerId(myCustomerId);
    // 고객사가 미리 설정된 경우 해당 고객사의 팀 목록을 로드
    if (myCustomerId) {
      teamApi.listByCustomer(myCustomerId).then(setInviteTeams).catch(() => setInviteTeams([]));
    }
    setShowInviteModal(true);
  };

  const handleInvite = async () => {
    const validEmails = inviteEmails.map(e => e.trim()).filter(e => e.length > 0);
    const errs = new Set<string>();
    if (needsTenant(inviteRole) && !inviteTenantId && isProductAdmin) errs.add('tenantId');
    if (needsCustomer(inviteRole) && !inviteCustomerId) errs.add('customerId');
    if (needsTeam(inviteRole) && !inviteTeamId) errs.add('teamId');
    if (validEmails.length === 0) errs.add('emails');
    if (errs.size > 0) { setInviteErrors(errs); return; }
    setInviteErrors(new Set());
    setInviting(true);
    try {
      const results = await authApi.inviteUsers(validEmails, inviteRole, inviteTenantId || undefined, inviteCustomerId || undefined, inviteTeamId || undefined);
      const failed = results.filter((r: any) => !r.success);
      const alreadyRegistered = results.filter((r: any) => !r.success && r.reason === 'already_registered');
      const sendFailed = results.filter((r: any) => !r.success && r.reason !== 'already_registered');

      if (failed.length === 0) {
        showToast('success', `${results.length}명에게 초대 메일을 발송했습니다.`);
        closeInviteModal();
      } else if (alreadyRegistered.length > 0 && sendFailed.length === 0) {
        const okCount = results.length - alreadyRegistered.length;
        showToast('error', `${okCount > 0 ? okCount + '명 발송 완료. ' : ''}이미 등록된 이메일: ${alreadyRegistered.map((r: any) => r.email).join(', ')}`);
        // 성공한 이메일 있으면 성공한 것만 제거, 실패한 이메일은 남김
        if (okCount > 0) {
          const failedEmails = alreadyRegistered.map((r: any) => r.email);
          setInviteEmails(failedEmails.length > 0 ? failedEmails : ['']);
        }
      } else {
        showToast('error', `${results.length - failed.length}명 성공, ${failed.length}명 실패 (${failed.map((r: any) => r.email).join(', ')})`);
      }
    } catch (e) {
      showToast('error', (e as any)?.response?.data?.error?.message || '초대 발송에 실패했습니다.');
    } finally { setInviting(false); }
  };

  // 입력 시 해당 필드 에러 해제
  const clearAddError = (field: string) => {
    setAddErrors((prev) => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const handleAdd = async () => {
    // 필수 입력 검증 — 미입력 필드를 모아 빨간 하이라이트 (토스트 없음)
    const errs = new Set<string>();
    if (!addForm.name.trim()) errs.add('name');
    if (!addForm.email.trim()) errs.add('email');
    if (!addForm.password.trim()) errs.add('password');
    if (!addForm.passwordConfirm.trim()) errs.add('passwordConfirm');
    if (addForm.password && addForm.passwordConfirm && addForm.password !== addForm.passwordConfirm) {
      errs.add('passwordConfirm');
    }
    if (needsTenant(addForm.role) && !addForm.tenantId) errs.add('tenantId');
    if (needsCustomer(addForm.role) && !addForm.customerId) errs.add('customerId');
    if (needsTeam(addForm.role) && !addForm.teamId) errs.add('teamId');
    if (errs.size > 0) {
      setAddErrors(errs);
      return;
    }
    setAddErrors(new Set());
    setAdding(true);
    try {
      await authApi.register({ name: addForm.name, email: addForm.email, password: addForm.password });
      // 등록 후 역할/소속/비고가 기본과 다르면 별도 업데이트
      const needsUpdate = addForm.role !== 'user' || addForm.tenantId || addForm.customerId || addForm.teamId || addForm.note.trim();
      if (needsUpdate) {
        const data = await authApi.listUsers();
        const created = data.find(u => u.email === addForm.email);
        if (created) {
          await authApi.updateUser(created.id, {
            role: addForm.role,
            tenantId: needsTenant(addForm.role) ? addForm.tenantId : '',
            customerId: needsCustomer(addForm.role) ? addForm.customerId : '',
            teamId: needsTeam(addForm.role) ? addForm.teamId : '',
            note: addForm.note.trim(),
          });
        }
      }
      showToast('success', '사용자가 등록되었습니다.');
      closeAddModal();
      loadUsers();
    } catch (error: unknown) {
      const axiosErr = error as { response?: { data?: { error?: { message?: string } } } };
      showToast('error', axiosErr?.response?.data?.error?.message || '등록에 실패했습니다.');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (userId: string, userName: string) => {
    setDeleteConfirm({ id: userId, name: userName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setDeletingId(deleteConfirm.id);
      await authApi.deleteUser(deleteConfirm.id);
      setUsers((prev) => prev.filter((u) => u.id !== deleteConfirm.id));
      showToast('success', '사용자가 삭제되었습니다.');
    } catch (error) {
      showToast('error', '사용자 삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
      setDeleteConfirm(null);
    }
  };

  const getRoleBadge = (role: string) => {
    const r = normalizeRole(role);
    // 관리자 계열은 admin 색(보라), 사용자는 user 색
    const variant = r === 'user' ? 'role-user' : 'role-admin';
    return <Badge variant={variant} shape="tag">{roleLabel(role)}</Badge>;
  };

  // 검색어 하이라이트 함수
  const highlightText = (text: string) => {
    if (!searchQuery.trim() || !text) return text;
    
    const query = searchQuery.toLowerCase();
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf(query);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);
    
    return (
      <>
        {before}
        <span className="search-highlight">{match}</span>
        {after}
      </>
    );
  };

  // 역할 필터 탭 (공통 Tabs)
  const roleTabItems: TabItem<'all' | UserRole>[] = [
    { key: 'all', label: '전체' },
    ...ROLE_OPTIONS.map((r) => ({ key: r, label: roleLabel(r) })),
  ];

  // 검색 (공통 SearchInput + 새로고침) - 탭 줄 오른쪽
  const searchBar = (
    <div className="users-search-bar">
      <SearchInput
        placeholder="이름, 이메일로 검색..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        onClear={() => setSearchQuery('')}
      />
      <button
        className="users-search-refresh"
        onClick={loadUsers}
        disabled={loading}
        title="새로고침"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8"/>
          <path d="M21 3v5h-5"/>
        </svg>
      </button>
    </div>
  );

  return (
    <>
      {!embedded && (
        <Topbar
          title="사용자 관리"
          description="시스템 사용자를 관리하고 역할을 설정합니다"
        />
      )}

      <div className={embedded ? '' : 'page-content'}>
        {/* 헤더: 왼쪽 역할 탭(공통 Tabs) + 우측 검색 · 사용자 등록 */}
        {!needsAdmin && (
          <div className="tabs-row">
            {users.length > 0 && (
              <Tabs items={roleTabItems} active={roleFilter} onChange={setRoleFilter} />
            )}
            <div className="tabs-row-aside admin-panel-actions users-tabs-aside">
              {isProductAdmin && tenants.length > 0 && (
                <Select value={tenantFilter} onChange={e => { setTenantFilter(e.target.value); }} style={{ minWidth: 140 }}>
                  <option value="">전체 테넌트</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              )}
              {users.length > 0 && searchBar}
              <Button color="gray" appearance="outline" onClick={openInviteModal}>✉ 사용자 초대</Button>
              <Button color="primary" onClick={openAddModal}>+ 사용자 등록</Button>
            </div>
          </div>
        )}
        <div className={embedded ? 'admin-section' : 'page-content-inner'}>
          {loading ? (
            <div className="loading">로딩 중...</div>
          ) : needsAdmin ? (
            <div className="empty-state">
              <p>관리자 권한이 필요합니다.</p>
              <p>시스템에 관리자가 없는 경우, 아래 버튼을 눌러 관리자로 승격할 수 있습니다.</p>
              <Button 
                onClick={handleMakeAdmin} 
                disabled={promoting}
                className="users-promote-btn"
              >
                {promoting ? '승격 중...' : '관리자로 승격하기'}
              </Button>
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <p>등록된 사용자가 없습니다.</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="empty-state">
              <p>"{searchQuery}"에 대한 검색 결과가 없습니다.</p>
            </div>
          ) : (
            <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th><SortableTh sort={sort} col="name">이름</SortableTh></th>
                  <th><SortableTh sort={sort} col="email">이메일</SortableTh></th>
                  <th><SortableTh sort={sort} col="role">역할</SortableTh></th>
                  <th><SortableTh sort={sort} col="tenant">테넌트</SortableTh></th>
                  <th><SortableTh sort={sort} col="customer">고객사</SortableTh></th>
                  <th><SortableTh sort={sort} col="team">팀</SortableTh></th>
                  <th>비고</th>
                  <th><SortableTh sort={sort} col="createdAt">가입일</SortableTh></th>
                  <th>작업</th>
                </tr>
              </thead>
              <tbody>
                {sort.sortRows(filteredUsers, (user, key) => {
                  if (key === 'role') return roleLabel(user.role);
                  if (key === 'tenant') return (user.tenantId && tenantNameMap.get(user.tenantId)) || '';
                  if (key === 'customer') return (user.customerId && customerNameMap.get(user.customerId)) || '';
                  if (key === 'team') return (user.teamId && teamNameMap.get(user.teamId)) || '';
                  return user[key] || '';
                }).map((user) => {
                  const isCurrentUser = user.id === currentUser?.id;
                  return (
                    <tr key={user.id}>
                      <td className="left">
                        {/* 이름 클릭 = 수정 버튼과 같은 상세/수정 모달 */}
                        <a href="#" className="table-link" onClick={(e) => { e.preventDefault(); handleOpenDetail(user); }}>
                          {highlightText(user.name)}
                        </a>
                        {isCurrentUser && (
                          <span className="current-user-badge">나</span>
                        )}
                      </td>
                      <td className="left">{highlightText(user.email)}</td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td>{user.tenantId ? (tenantNameMap.get(user.tenantId) || '-') : '-'}</td>
                      <td>{user.customerId ? (customerNameMap.get(user.customerId) || '-') : '-'}</td>
                      <td>{user.teamId ? (teamNameMap.get(user.teamId) || '-') : '-'}</td>
                      <td className="user-note-cell" title={user.note || ''}>{user.note || '-'}</td>
                      <td>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : '-'}
                      </td>
                      <td>
                        <div className="user-actions">
                          <button
                            className="icon-action-btn"
                            onClick={() => handleOpenDetail(user)}
                            title="수정"
                          >
                            <EditIcon />
                          </button>
                          {!isCurrentUser && (
                            <button
                              className="icon-action-btn delete"
                              onClick={() => handleDelete(user.id, user.name)}
                              disabled={deletingId !== null}
                              title="삭제"
                            >
                              <TrashIcon />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {/* 사용자 상세/수정 모달 - 공통 Modal + 공통 입력 폼 (메뉴 관리 모달과 동일) */}
      <Modal
        isOpen={!!selectedUser}
        onClose={handleCloseDetail}
        title="사용자 상세 정보"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={handleCloseDetail}>취소</Button>
            <Button color="primary" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          </>
        }
      >
        {selectedUser && (
          <div className="form-grid form-grid--2">
            {/* 이메일 — 풀 너비 */}
            <div className="form-item form-item--full">
              <div className="form-label">이메일</div>
              <div className="input">
                <input className="input-control" type="email" value={selectedUser.email} disabled />
              </div>
            </div>
            {/* 이름 | 역할 — 나란히 */}
            <div className="form-item">
              <div className="form-label">이름</div>
              <div className="input">
                <input
                  className="input-control"
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                />
              </div>
            </div>
            <div className="form-item">
              <div className="form-label">
                역할
                {selectedUser.id === currentUser?.id && <span className="optional">본인 계정은 변경 불가</span>}
              </div>
              <Select
                value={editForm.role}
                onChange={(e) => {
                  const role = e.target.value as UserRole;
                  setEditForm((prev) => ({
                    ...prev,
                    role,
                    tenantId: needsTenant(role) ? prev.tenantId : '',
                    customerId: needsCustomer(role) ? prev.customerId : '',
                    teamId: needsTeam(role) ? prev.teamId : '',
                  }));
                  // 팀 관리자로 변경 시 현재 선택된 고객사 기준 팀 목록 로드
                  if (role === 'teamAdmin' && editForm.customerId) {
                    teamApi.listByCustomer(editForm.customerId).then(setTeams).catch(() => setTeams([]));
                  }
                }}
                disabled={selectedUser.id === currentUser?.id}
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>{roleLabel(r)}</option>
                ))}
              </Select>
            </div>

            {/* 소속 테넌트 | 소속 고객사 — 나란히 */}
            {needsTenant(editForm.role) && (
              <div className="form-item">
                <div className="form-label">소속 테넌트</div>
                <Select
                  value={editForm.tenantId}
                  onChange={(e) => setEditForm({ ...editForm, tenantId: e.target.value, customerId: '' })}
                >
                  <option value="">테넌트 선택</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              </div>
            )}

            {needsCustomer(editForm.role) && (
              <div className="form-item">
                <div className="form-label">소속 고객사</div>
                <Select
                  value={editForm.customerId}
                  onChange={(e) => {
                    const cid = e.target.value;
                    setEditForm({ ...editForm, customerId: cid, teamId: '' });
                    if (cid && needsTeam(editForm.role)) {
                      teamApi.listByCustomer(cid).then(setTeams).catch(() => setTeams([]));
                    }
                  }}
                  disabled={!editForm.tenantId}
                >
                  <option value="">{editForm.tenantId ? '고객사 선택' : '테넌트를 먼저 선택'}</option>
                  {customers.filter((c) => c.tenantId === editForm.tenantId).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </Select>
              </div>
            )}

            {/* 소속 팀 — 조건부, 홀수 배치 시 풀 너비로 */}
            {needsTeam(editForm.role) && (
              <div className={`form-item${!needsTenant(editForm.role) || !needsCustomer(editForm.role) ? ' form-item--full' : ''}`}>
                <div className="form-label">소속 팀 <span className="required">*</span></div>
                <Select
                  value={editForm.teamId}
                  onChange={(e) => setEditForm({ ...editForm, teamId: e.target.value })}
                  disabled={!editForm.customerId}
                >
                  <option value="">{editForm.customerId ? '팀 선택' : '고객사를 먼저 선택'}</option>
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              </div>
            )}

            {/* 비고 — 풀 너비 */}
            <div className="form-item form-item--full">
              <div className="form-label" style={{ marginBottom: 20 }}>비고</div>
              <div className="input">
                <textarea
                  className="input-control"
                  placeholder="비고 입력"
                  rows={2}
                  value={editForm.note}
                  onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                  style={{ resize: 'vertical' }}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* 사용자 등록 모달 - 공통 Modal + 공통 입력 폼 */}
      <Modal
        isOpen={showAddModal}
        onClose={closeAddModal}
        title="사용자 등록"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeAddModal}>취소</Button>
            <Button color="primary" onClick={handleAdd} disabled={adding}>
              {adding ? '등록 중...' : '등록'}
            </Button>
          </>
        }
      >
        <div className="form-grid form-grid--2">
          {/* 이름 | 역할 — 나란히 */}
          <div className="form-item">
            <div className="form-label">이름 <span className="required">*</span></div>
            <div className={`input${addErrors.has('name') ? ' has-error' : ''}`}>
              <input className="input-control" type="text" placeholder="이름 입력"
                value={addForm.name}
                onChange={e => { setAddForm({ ...addForm, name: e.target.value }); clearAddError('name'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">역할</div>
            <Select
              value={addForm.role}
              onChange={e => {
                const role = e.target.value as UserRole;
                setAddForm((prev) => ({
                  ...prev,
                  role,
                  tenantId: needsTenant(role) ? prev.tenantId : '',
                  customerId: needsCustomer(role) ? prev.customerId : '',
                  teamId: needsTeam(role) ? prev.teamId : '',
                }));
                if (role === 'teamAdmin' && addForm.customerId) {
                  teamApi.listByCustomer(addForm.customerId).then(setTeams).catch(() => setTeams([]));
                }
              }}
            >
              {(isProductAdmin
                ? ROLE_OPTIONS
                : isCustomerAdmin
                  ? (['teamAdmin', 'user'] as UserRole[])
                  : (['customerAdmin', 'teamAdmin', 'user'] as UserRole[])
              ).map((r) => (
                <option key={r} value={r}>{roleLabel(r)}</option>
              ))}
            </Select>
          </div>

          {/* 이메일 — 풀 너비 */}
          <div className="form-item form-item--full">
            <div className="form-label">이메일 <span className="required">*</span></div>
            <div className={`input${addErrors.has('email') ? ' has-error' : ''}`}>
              <input className="input-control" type="email" placeholder="example@company.com" autoComplete="off"
                value={addForm.email}
                onChange={e => { setAddForm({ ...addForm, email: e.target.value }); clearAddError('email'); }} />
            </div>
          </div>

          {/* 비밀번호 | 비밀번호 확인 — 나란히 */}
          <div className="form-item">
            <div className="form-label">비밀번호 <span className="required">*</span></div>
            <div className={`input${addErrors.has('password') ? ' has-error' : ''}`}>
              <input className="input-control" type="password" placeholder="비밀번호 입력" autoComplete="new-password"
                value={addForm.password}
                onChange={e => { setAddForm({ ...addForm, password: e.target.value }); clearAddError('password'); clearAddError('passwordConfirm'); }} />
            </div>
          </div>
          <div className="form-item">
            <div className="form-label">비밀번호 확인 <span className="required">*</span></div>
            <div className={`input${addErrors.has('passwordConfirm') ? ' has-error' : ''}`}>
              <input className="input-control" type="password" placeholder="비밀번호 재입력" autoComplete="new-password"
                value={addForm.passwordConfirm}
                onChange={e => {
                  const val = e.target.value;
                  setAddForm(prev => ({ ...prev, passwordConfirm: val }));
                  setAddErrors(prev => {
                    const next = new Set(prev);
                    if (val && addForm.password !== val) next.add('passwordConfirm');
                    else next.delete('passwordConfirm');
                    return next;
                  });
                }} />
            </div>
            {addErrors.has('passwordConfirm') && addForm.passwordConfirm ? (
              <span style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>비밀번호가 일치하지 않습니다.</span>
            ) : addForm.passwordConfirm && addForm.password === addForm.passwordConfirm ? (
              <span style={{ fontSize: 12, color: 'var(--success, #16a34a)', marginTop: 4 }}>비밀번호가 일치합니다.</span>
            ) : null}
          </div>

          {/* 소속 테넌트 | 소속 고객사 — 나란히 */}
          {needsTenant(addForm.role) && (
            <div className={`form-item${addErrors.has('tenantId') ? ' has-error' : ''}`}>
              <div className="form-label">소속 테넌트 <span className="required">*</span></div>
              <Select
                value={addForm.tenantId}
                onChange={(e) => { setAddForm({ ...addForm, tenantId: e.target.value, customerId: '' }); clearAddError('tenantId'); }}
                disabled={!isProductAdmin}
              >
                <option value="">테넌트 선택</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
            </div>
          )}

          {needsCustomer(addForm.role) && (
            <div className={`form-item${addErrors.has('customerId') ? ' has-error' : ''}`}>
              <div className="form-label">소속 고객사 <span className="required">*</span></div>
              <Select
                value={addForm.customerId}
                onChange={(e) => {
                  const cid = e.target.value;
                  setAddForm({ ...addForm, customerId: cid, teamId: '' });
                  clearAddError('customerId');
                  if (cid && needsTeam(addForm.role)) {
                    teamApi.listByCustomer(cid).then(setTeams).catch(() => setTeams([]));
                  }
                }}
                disabled={isCustomerAdmin || !addForm.tenantId}
              >
                <option value="">{addForm.tenantId ? '고객사 선택' : '테넌트를 먼저 선택'}</option>
                {customers.filter((c) => !addForm.tenantId || c.tenantId === addForm.tenantId).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>
          )}

          {/* 소속 팀 — 조건부 */}
          {needsTeam(addForm.role) && (
            <div className={`form-item${addErrors.has('teamId') ? ' has-error' : ''}${!needsTenant(addForm.role) || !needsCustomer(addForm.role) ? ' form-item--full' : ''}`}>
              <div className="form-label">소속 팀 <span className="required">*</span></div>
              <Select
                value={addForm.teamId}
                onChange={(e) => { setAddForm({ ...addForm, teamId: e.target.value }); clearAddError('teamId'); }}
                disabled={!addForm.customerId}
              >
                <option value="">{addForm.customerId ? '팀 선택' : '고객사를 먼저 선택'}</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Select>
            </div>
          )}

          {/* 비고 — 풀 너비 */}
          <div className="form-item form-item--full">
            <div className="form-label" style={{ marginBottom: 20 }}>비고</div>
            <div className="input">
              <textarea className="input-control" placeholder="비고 입력" rows={2}
                value={addForm.note} onChange={e => setAddForm({ ...addForm, note: e.target.value })}
                style={{ resize: 'vertical' }} />
            </div>
          </div>
        </div>
      </Modal>

      {/* 사용자 삭제 확인 모달 */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="사용자 삭제"
        size="sm"
        style={{ maxWidth: 360 }}
        bodyClassName="dialog-body--compact"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={() => setDeleteConfirm(null)}>취소</Button>
            <Button color="error" onClick={confirmDelete} disabled={deletingId !== null}>삭제</Button>
          </>
        }
      >
        <p style={{ margin: '4px 0 12px', lineHeight: 1.6, textAlign: 'center' }}>
          "{deleteConfirm?.name}" 사용자를 삭제하시겠습니까?<br />삭제 후 복구할 수 없습니다.
        </p>
      </Modal>

      {/* 사용자 초대 모달 */}
      <Modal
        isOpen={showInviteModal}
        onClose={closeInviteModal}
        title="사용자 초대"
        size="sm"
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeInviteModal}>취소</Button>
            <Button color="primary" onClick={handleInvite} disabled={inviting}>
              {inviting ? '발송 중...' : '초대 메일 발송'}
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <div className="form-item">
            <div className="form-label">권한</div>
            <Select value={inviteRole} onChange={e => {
              const role = e.target.value as UserRole;
              setInviteRole(role);
              setInviteCustomerId(isCustomerAdmin ? myCustomerId : '');
              setInviteTeamId('');
              if (isCustomerAdmin && myCustomerId && needsTeam(role)) {
                teamApi.listByCustomer(myCustomerId).then(setInviteTeams).catch(() => setInviteTeams([]));
              }
            }}>
              {(isProductAdmin
                ? ['productAdmin', 'tenantAdmin', 'customerAdmin', 'teamAdmin', 'user']
                : isCustomerAdmin ? ['teamAdmin', 'user'] : ['customerAdmin', 'teamAdmin', 'user']
              ).map((r) => (
                <option key={r} value={r}>{roleLabel(r as UserRole)}</option>
              ))}
            </Select>
          </div>
          {needsTenant(inviteRole) && (
            <div className={`form-item${inviteErrors.has('tenantId') ? ' has-error' : ''}`}>
              <div className="form-label">소속 테넌트 {isProductAdmin && <span className="required">*</span>}</div>
              <Select value={inviteTenantId} onChange={e => { setInviteTenantId(e.target.value); setInviteCustomerId(''); setInviteTeamId(''); setInviteErrors(prev => { const n=new Set(prev); n.delete('tenantId'); return n; }); }} disabled={!isProductAdmin}>
                <option value="">테넌트 선택</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </div>
          )}
          {needsCustomer(inviteRole) && (
            <div className={`form-item${inviteErrors.has('customerId') ? ' has-error' : ''}`}>
              <div className="form-label">소속 고객사 <span className="required">*</span></div>
              <Select
                value={inviteCustomerId}
                onChange={e => {
                  const cid = e.target.value;
                  setInviteCustomerId(cid);
                  setInviteTeamId('');
                  setInviteErrors(prev => { const n=new Set(prev); n.delete('customerId'); return n; });
                  if (cid && needsTeam(inviteRole)) {
                    teamApi.listByCustomer(cid).then(setInviteTeams).catch(() => setInviteTeams([]));
                  }
                }}
                disabled={isCustomerAdmin}
              >
                <option value="">{inviteTenantId ? '고객사 선택' : '테넌트를 먼저 선택'}</option>
                {customers.filter(c => !inviteTenantId || c.tenantId === inviteTenantId).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>
          )}
          {needsTeam(inviteRole) && (
            <div className={`form-item${inviteErrors.has('teamId') ? ' has-error' : ''}`}>
              <div className="form-label">소속 팀 <span className="required">*</span></div>
              <Select
                value={inviteTeamId}
                onChange={e => { setInviteTeamId(e.target.value); setInviteErrors(prev => { const n=new Set(prev); n.delete('teamId'); return n; }); }}
                disabled={!inviteCustomerId}
              >
                <option value="">{inviteCustomerId ? '팀 선택' : '고객사를 먼저 선택'}</option>
                {inviteTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </div>
          )}
          <div className={`form-item${inviteErrors.has('emails') ? ' has-error' : ''}`}>
            <div className="form-label">이메일 <span className="required">*</span></div>
            {inviteEmails.map((email, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <div className="input" style={{ flex: 1 }}>
                  <input className="input-control" type="email" placeholder={`이메일 ${idx + 1}`} value={email}
                    onChange={e => {
                      const next = [...inviteEmails];
                      next[idx] = e.target.value;
                      setInviteEmails(next);
                      if (e.target.value.trim()) setInviteErrors(prev => { const n=new Set(prev); n.delete('emails'); return n; });
                    }} />
                </div>
                <button type="button" style={{ padding: '0 10px', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', background: '#f5f5f5' }}
                  onClick={() => setInviteEmails(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)}>
                  −
                </button>
              </div>
            ))}
            <button type="button" style={{ fontSize: 13, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={() => setInviteEmails(prev => [...prev, ''])}>
              + 이메일 추가
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default UsersPage;
